import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useBoolean } from '@fluentui/react-hooks';
import {
    Button,
    Spinner,
    SearchBox,
    Badge,
    Tooltip,
} from '@fluentui/react-components';
import {
    Add20Regular,
    EditRegular,
    DeleteRegular,
    ArrowSortDownRegular,
    ArrowSortRegular,
    ArrowSortUpRegular,
    BuildingMultiple20Regular,
    Layer20Regular,
    AppsListDetail20Regular,
    ProhibitedRegular,
} from '@fluentui/react-icons';

import ConceptFormComponent from '../ConceptForm/ConceptForm.Component';
import { ErrorMessageComponent } from '../../../Error/ErrorMessageComponent';

import { ContractConcept } from '../../../../models/Entities/Contracts/ContractConcept';
import { ContractStage } from '../../../../models/Entities/Contracts/ContractStage';
import { NewContractConceptPaymentSchedule } from '../../../../models/Entities/Contracts/NewContractConceptPaymentSchedule';

import * as Fn from './ConceptGroupedView.Functions';
import {
    getGroupForConcept,
    getSettlementLabel,
    getValuationLabel,
    formatCurrencyAmount,
    formatCoefficient,
    ConceptValuationOptionsEnum,
    SettlementOptionsEnum,
} from './ConceptGroupedView.Functions';

import './ConceptGroupedView.Styles.css';

type SortMode = 'load' | 'alpha-asc' | 'alpha-desc';

const sortConcepts = (concepts: ContractConcept[], mode: SortMode): ContractConcept[] => {
    if (mode === 'load') return concepts;
    const sorted = [...concepts].sort((a, b) => {
        const an = (a.concept?.conceptName || '').toString();
        const bn = (b.concept?.conceptName || '').toString();
        return an.localeCompare(bn, undefined, { sensitivity: 'base', numeric: true });
    });
    return mode === 'alpha-asc' ? sorted : sorted.reverse();
};

const SortToggle: React.FC<{ mode: SortMode; onChange: (m: SortMode) => void; t: any }> = ({ mode, onChange, t }) => {
    const cycle = () => {
        if (mode === 'load') onChange('alpha-asc');
        else if (mode === 'alpha-asc') onChange('alpha-desc');
        else onChange('load');
    };
    let label: string;
    let icon: React.ReactElement;
    if (mode === 'load') {
        label = 'Orden de carga';
        icon = <ArrowSortRegular />;
    } else if (mode === 'alpha-asc') {
        label = 'A → Z';
        icon = <ArrowSortDownRegular />;
    } else {
        label = 'Z → A';
        icon = <ArrowSortUpRegular />;
    }
    return (
        <Tooltip content={`Ordenar (${label})`} relationship='label' withArrow>
            <Button
                appearance='subtle'
                size='small'
                icon={icon}
                onClick={cycle}
                className='cgv-sort-btn'
            >
                {label}
            </Button>
        </Tooltip>
    );
};

const matchesText = (cc: ContractConcept, term: string): boolean => {
    if (!term) return true;
    const n = (cc.concept?.conceptName || '').toString().toLowerCase();
    const d = (cc.concept?.conceptDescription || '').toString().toLowerCase();
    const r = (cc.concept?.conceptReferenceId as any || '').toString().toLowerCase();
    return n.includes(term) || d.includes(term) || r.includes(term);
};

// ──────────────────────────────────────────────────────────
// Execution columns used in ByStage matrix
// ──────────────────────────────────────────────────────────
type ExecutionKey = 'inFace' | 'remote' | 'phone';
interface ExecutionDef {
    key: ExecutionKey;
    short: string;
    label: string;
}
const allExecutions: ExecutionDef[] = [
    { key: 'inFace', short: 'Pres.', label: 'Presencial' },
    { key: 'remote', short: 'Rem.', label: 'Remoto' },
    { key: 'phone', short: 'Tel.', label: 'Telefónico' },
];

const conceptApplies = (cc: ContractConcept, ex: ExecutionKey): boolean => {
    if (ex === 'inFace') return !!cc.applyToInFaceExecution;
    if (ex === 'remote') return !!cc.applyToRemoteExecution;
    return !!cc.applyToPhoneExecution;
};

const getStageScheduleFor = (cc: ContractConcept, stageId: number): NewContractConceptPaymentSchedule | undefined => {
    console.log(`debug getStageScheduleFor: conceptId=${cc.id}, stageId=${stageId}, schedule=${JSON.stringify(cc.paymentSchedule)}`);
    let response = cc.paymentSchedule?.find(ps => (ps as any).stage?.id === stageId || ps.contractStageId === stageId);
    console.log('debug getStageScheduleFor: ' + JSON.stringify(response));
    return response;
};

const getPriceForExecution = (sch: NewContractConceptPaymentSchedule | undefined, ex: ExecutionKey): number | undefined => {
    if (!sch) return undefined;
    if (ex === 'inFace') return sch.price;
    if (ex === 'remote') return sch.remotePrice;
    return sch.phonePrice;
};

const getCoefficientForExecution = (sch: NewContractConceptPaymentSchedule | undefined, ex: ExecutionKey): number | undefined => {
    if (!sch) return undefined;
    if (ex === 'inFace') return sch.settlementCoefficient;
    if (ex === 'remote') return sch.settlementRemoteCoefficient;
    return sch.settlementPhoneCoefficient;
};

// ──────────────────────────────────────────────────────────
// Generic (non-ByStage) table
// ──────────────────────────────────────────────────────────
interface GenericTableProps {
    title: string;
    icon: React.ReactNode;
    accent: string;
    concepts: ContractConcept[];
    onEdit: (c: ContractConcept) => void;
    onDelete: (c: ContractConcept) => void;
}

const GenericTable: React.FC<GenericTableProps> = ({ title, icon, accent, concepts, onEdit, onDelete }) => {
    const [filter, setFilter] = useState('');
    const [sortMode, setSortMode] = useState<SortMode>('load');
    const { t } = useTranslation();

    const visible = useMemo(() => {
        const term = filter.trim().toLowerCase();
        return sortConcepts(concepts.filter(c => matchesText(c, term)), sortMode);
    }, [concepts, filter, sortMode]);

    return (
        <section className='cgv-card' style={{ ['--cgv-accent' as any]: accent }}>
            <header className='cgv-card__header'>
                <span className='cgv-card__icon'>{icon}</span>
                <h3 className='cgv-card__title'>{title}</h3>
                <span className='cgv-card__count'>{concepts.length}</span>
                <div className='cgv-card__actions'>
                    <SortToggle mode={sortMode} onChange={setSortMode} t={t} />
                    <SearchBox
                        size='small'
                        placeholder='Buscar…'
                        value={filter}
                        onChange={(_, d) => setFilter(d.value || '')}
                        className='cgv-search'
                    />
                </div>
            </header>

            {visible.length === 0
                ? <div className='cgv-empty'>Sin conceptos en esta categoría.</div>
                : (
                    <div className='cgv-table-wrap'>
                        <table className='cgv-table'>
                            <thead>
                                <tr>
                                    <th className='cgv-th cgv-th--name'>Concepto</th>
                                    <th className='cgv-th'>Liquidación</th>
                                    <th className='cgv-th'>Valuación</th>
                                    <th className='cgv-th cgv-th--num'>Precio</th>
                                    <th className='cgv-th'>Ejecución</th>
                                    <th className='cgv-th cgv-th--actions'>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map(cc => (
                                    <tr key={cc.id} className='cgv-tr'>
                                        <td className='cgv-td cgv-td--name'>
                                            <div className='cgv-concept'>
                                                <span className='cgv-concept__name'>{cc.concept?.conceptName || '—'}</span>
                                                {cc.concept?.conceptReferenceId && (
                                                    <span className='cgv-concept__ref'>#{cc.concept.conceptReferenceId as any}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className='cgv-td'>
                                            <Badge appearance='outline' size='small'>{getSettlementLabel(cc.settlementSetting)}</Badge>
                                        </td>
                                        <td className='cgv-td'>
                                            <Badge appearance='tint' size='small'>{getValuationLabel(cc.valuationType)}</Badge>
                                        </td>
                                        <td className='cgv-td cgv-td--num cgv-amount'>
                                            {formatCurrencyAmount(cc.priceValue, cc.conceptCurrency)}
                                        </td>
                                        <td className='cgv-td'>
                                            <div className='cgv-exec'>
                                                {cc.applyToInFaceExecution && <span className='cgv-exec__pill cgv-exec__pill--inFace' title='Presencial'>Pres.</span>}
                                                {cc.applyToRemoteExecution && <span className='cgv-exec__pill cgv-exec__pill--remote' title='Remoto'>Rem.</span>}
                                                {cc.applyToPhoneExecution && <span className='cgv-exec__pill cgv-exec__pill--phone' title='Telefónico'>Tel.</span>}
                                            </div>
                                        </td>
                                        <td className='cgv-td cgv-td--actions'>
                                            <Button appearance='subtle' size='small' icon={<EditRegular />} aria-label='Editar' onClick={() => onEdit(cc)} />
                                            <Button appearance='subtle' size='small' icon={<DeleteRegular />} aria-label='Eliminar' onClick={() => onDelete(cc)} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
        </section>
    );
};

// ──────────────────────────────────────────────────────────
// ByStage matrix table (sticky concept column + scrollable stages)
// ──────────────────────────────────────────────────────────
interface ByStageTableProps {
    title: string;
    accent: string;
    concepts: ContractConcept[];
    stages: ContractStage[];
    onEdit: (c: ContractConcept) => void;
    onDelete: (c: ContractConcept) => void;
}

const ByStageTable: React.FC<ByStageTableProps> = ({ title, accent, concepts, stages, onEdit, onDelete }) => {
    const [filter, setFilter] = useState('');
    const [sortMode, setSortMode] = useState<SortMode>('load');
    const { t } = useTranslation();

    const orderedStages = useMemo(() => {
        return [...(stages || [])].sort((a, b) => (a.stageOrder ?? 0) - (b.stageOrder ?? 0));
    }, [stages]);

    const visible = useMemo(() => {
        const term = filter.trim().toLowerCase();
        return sortConcepts(concepts.filter(c => matchesText(c, term)), sortMode);
    }, [concepts, filter, sortMode]);

    return (
        <section className='cgv-card cgv-card--matrix' style={{ ['--cgv-accent' as any]: accent }}>
            <header className='cgv-card__header'>
                <span className='cgv-card__icon'><Layer20Regular /></span>
                <h3 className='cgv-card__title'>{title}</h3>
                <span className='cgv-card__count'>{concepts.length}</span>
                <div className='cgv-card__actions'>
                    <SortToggle mode={sortMode} onChange={setSortMode} t={t} />
                    <SearchBox
                        size='small'
                        placeholder='Buscar…'
                        value={filter}
                        onChange={(_, d) => setFilter(d.value || '')}
                        className='cgv-search'
                    />
                </div>
            </header>

            {visible.length === 0
                ? <div className='cgv-empty'>Sin conceptos por etapa.</div>
                : (
                    <div className='cgv-matrix-wrap'>
                        <table className='cgv-matrix'>
                            <thead>
                                <tr>
                                    <th className='cgv-th cgv-th--name cgv-sticky-col'>Concepto</th>
                                    <th className='cgv-th cgv-th--num cgv-sticky-col cgv-sticky-col--2'>Precio base</th>
                                    {orderedStages.map(st => (
                                        <th key={st.id} className='cgv-th cgv-th--stage' colSpan={3}>
                                            <div className='cgv-stage-head'>
                                                <span className='cgv-stage-head__name' title={st.stageName}>{st.stageName}</span>
                                            </div>
                                        </th>
                                    ))}
                                    <th className='cgv-th cgv-th--actions cgv-sticky-col-right'>Acciones</th>
                                </tr>
                                <tr className='cgv-subhead'>
                                    <th className='cgv-th cgv-sticky-col' />
                                    <th className='cgv-th cgv-sticky-col cgv-sticky-col--2' />
                                    {orderedStages.flatMap(st => allExecutions.map(ex => (
                                        <th key={`${st.id}-${ex.key}`} className={`cgv-th cgv-th--exec cgv-th--exec-${ex.key}`}>{ex.short}</th>
                                    )))}
                                    <th className='cgv-th cgv-sticky-col-right' />
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map(cc => {
                                    // Cell render mode per concept:
                                    //  - ValueByStage           → show price per stage, hide base price
                                    //  - FixedValue + ByStage   → show coefficient pill per stage, base price visible
                                    //  - otherwise (has schedule) → show price per stage, base price visible
                                    const isValueByStage = cc.valuationType === ConceptValuationOptionsEnum.ValueByStage;
                                    const isFixedByStage = cc.valuationType === ConceptValuationOptionsEnum.FixedValue
                                        && cc.settlementSetting === SettlementOptionsEnum.ByStage;
                                    const cellMode: 'price' | 'coefficient' = isFixedByStage ? 'coefficient' : 'price';
                                    return (
                                        <tr key={cc.id} className='cgv-tr'>
                                            <td className='cgv-td cgv-td--name cgv-sticky-col'>
                                                <div className='cgv-concept'>
                                                    <span className='cgv-concept__name'>{cc.concept?.conceptName || '—'}</span>
                                                    <span className='cgv-concept__meta'>
                                                        <Badge appearance='outline' size='small'>{getSettlementLabel(cc.settlementSetting)}</Badge>
                                                        <Badge appearance='tint' size='small'>{getValuationLabel(cc.valuationType)}</Badge>
                                                    </span>
                                                </div>
                                            </td>
                                            <td className='cgv-td cgv-td--num cgv-sticky-col cgv-sticky-col--2 cgv-amount'>
                                                {isValueByStage
                                                    ? <span className='cgv-muted'>—</span>
                                                    : formatCurrencyAmount(cc.priceValue, cc.conceptCurrency)}
                                            </td>
                                            {orderedStages.flatMap(st => {
                                                const sch = getStageScheduleFor(cc, st.id);
                                                console.log(`[CGV cell] conceptId=${cc.id} stageId=${st.id} cellMode=${cellMode} sch=${JSON.stringify(sch)}`);
                                                return allExecutions.map(ex => {
                                                    // For ByStage concepts the applyTo* flags may not be set;
                                                    // if a schedule entry exists for this stage, treat all executions as applicable.
                                                    const applies = !!sch || conceptApplies(cc, ex.key);
                                                    console.log(`[CGV cell] conceptId=${cc.id} stageId=${st.id} ex=${ex.key} applies=${applies}`);
                                                    if (!applies) {
                                                        return <td key={`${st.id}-${ex.key}`} className={`cgv-td cgv-td--cell cgv-td--cell-${ex.key} cgv-cell--na`} title='No aplica'>·</td>;
                                                    }
                                                    if (cellMode === 'coefficient') {
                                                        const coef = getCoefficientForExecution(sch, ex.key);
                                                        const hasCoef = coef !== undefined && coef !== null && !Number.isNaN(coef);
                                                        console.log(`[CGV cell] conceptId=${cc.id} stageId=${st.id} ex=${ex.key} coef=${coef} hasCoef=${hasCoef}`);
                                                        return (
                                                            <td key={`${st.id}-${ex.key}`} className={`cgv-td cgv-td--cell cgv-td--cell-${ex.key}`}>
                                                                {!hasCoef
                                                                    ? <span className='cgv-muted'>—</span>
                                                                    : coef === 0
                                                                        ? <span className='cgv-coef cgv-coef--na' title={`No aplica (${ex.label})`}><ProhibitedRegular /></span>
                                                                        : <span className='cgv-coef' title={`Coeficiente ${ex.label}`}>x {formatCoefficient(coef)}</span>}
                                                            </td>
                                                        );
                                                    }
                                                    const price = getPriceForExecution(sch, ex.key);
                                                    const hasPrice = price !== undefined && price !== null;
                                                    console.log(`[CGV cell] conceptId=${cc.id} stageId=${st.id} ex=${ex.key} price=${price} hasPrice=${hasPrice}`);
                                                    return (
                                                        <td key={`${st.id}-${ex.key}`} className={`cgv-td cgv-td--cell cgv-td--cell-${ex.key}`}>
                                                            <span className='cgv-cell-price' title={ex.label}>
                                                                {hasPrice
                                                                    ? formatCurrencyAmount(price, sch?.currency || cc.conceptCurrency)
                                                                    : <span className='cgv-muted'>—</span>}
                                                            </span>
                                                        </td>
                                                    );
                                                });
                                            })}
                                            <td className='cgv-td cgv-td--actions cgv-sticky-col-right'>
                                                <Button appearance='subtle' size='small' icon={<EditRegular />} aria-label='Editar' onClick={() => onEdit(cc)} />
                                                <Button appearance='subtle' size='small' icon={<DeleteRegular />} aria-label='Eliminar' onClick={() => onDelete(cc)} />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
        </section>
    );
};

// ──────────────────────────────────────────────────────────
// Root component
// ──────────────────────────────────────────────────────────
const ConceptGroupedViewComponent = (props: Fn.Props) => {
    const [isFormOpen, { setTrue: openForm, setFalse: closeForm }] = useBoolean(false);
    const [selectedConcept, setSelectedConcept] = useState<ContractConcept | undefined>(undefined);
    const [globalFilter, setGlobalFilter] = useState('');

    useEffect(() => {
        if (props.Contract) {
            props.GetAllContractsConceptByAddendum(props.ContractId, props.ContractAddendumId);
        }
    }, [props.Contract === undefined, props.ContractAddendumId]);

    // Ensure stages are loaded for the current addendum (needed for ByStage matrix columns)
    useEffect(() => {
        const stagesLoaded = Array.isArray(props.Addendum?.stages) && (props.Addendum?.stages?.length || 0) > 0;
        if (props.ContractAddendumId && !stagesLoaded) {
            props.GetAllContractStagesByAddendumId(props.ContractId, props.ContractAddendumId);
        }
    }, [props.ContractId, props.ContractAddendumId, props.Addendum?.stages?.length]);

    // Lazy-load per-stage payment schedules for ALL concepts that don't have them yet
    const requestedScheduleIdsRef = React.useRef<Set<number>>(new Set());
    useEffect(() => {
        if (!props.ContractConcepts || props.ContractConcepts.length === 0) return;
        props.ContractConcepts.forEach(cc => {
            const hasSchedule = Array.isArray(cc.paymentSchedule) && cc.paymentSchedule.length > 0;
            if (hasSchedule) return;
            if (requestedScheduleIdsRef.current.has(cc.id)) return;
            requestedScheduleIdsRef.current.add(cc.id);
            props.GetAllContractsConceptPaymentSchedule(props.ContractId, props.ContractAddendumId, cc.id);
        });
    }, [props.ContractConcepts, props.ContractId, props.ContractAddendumId]);

    const handleAdd = () => {
        setSelectedConcept(undefined);
        openForm();
    };
    const handleEdit = (c: ContractConcept) => {
        setSelectedConcept(c);
        openForm();
    };
    const handleDelete = (c: ContractConcept) => {
        props.DeleteContractConcept(c.id, props.ContractId, props.ContractAddendumId);
    };
    const handleFormDismiss = () => {
        setSelectedConcept(undefined);
        closeForm();
    };

    // Apply global filter once, then bucket
    const globallyFiltered = useMemo(() => {
        const term = globalFilter.trim().toLowerCase();
        if (!term) return props.ContractConcepts;
        return props.ContractConcepts.filter(c => matchesText(c, term));
    }, [props.ContractConcepts, globalFilter]);

    const buckets = useMemo(() => {
        const byStage: ContractConcept[] = [];
        const inAnyStage: ContractConcept[] = [];
        const others: ContractConcept[] = [];
        for (const c of globallyFiltered) {
            const g = getGroupForConcept(c);
            if (g === 'byStage') byStage.push(c);
            else if (g === 'inAnyStage') inAnyStage.push(c);
            else others.push(c);
        }
        return { byStage, inAnyStage, others };
    }, [globallyFiltered]);

    const stages: ContractStage[] = (props.Addendum?.stages || []) as any;

    return (
        <div className='cgv-root'>
            <div className='cgv-toolbar'>
                <Button appearance='primary' icon={<Add20Regular />} onClick={handleAdd}>
                    Agregar concepto
                </Button>
                <SearchBox
                    placeholder='Buscar en todas las tablas…'
                    value={globalFilter}
                    onChange={(_, d) => setGlobalFilter(d.value || '')}
                    className='cgv-global-search'
                />
                {props.isLoading && (
                    <span className='cgv-loading'>
                        <Spinner size='extra-tiny' /> <span>Cargando…</span>
                    </span>
                )}
            </div>

            {props.failOnLoading && props.error && <ErrorMessageComponent Error={props.error} />}

            <ByStageTable
                title='Conceptos por etapa'
                accent='#7c3aed'
                concepts={buckets.byStage}
                stages={stages}
                onEdit={handleEdit}
                onDelete={handleDelete}
            />

            <GenericTable
                title='Conceptos en cualquier etapa'
                icon={<BuildingMultiple20Regular />}
                accent='#2563eb'
                concepts={buckets.inAnyStage}
                onEdit={handleEdit}
                onDelete={handleDelete}
            />

            <GenericTable
                title='Otros conceptos'
                icon={<AppsListDetail20Regular />}
                accent='#20ad9b'
                concepts={buckets.others}
                onEdit={handleEdit}
                onDelete={handleDelete}
            />

            <ConceptFormComponent
                isOpen={isFormOpen}
                openPanel={openForm}
                dismissPanel={handleFormDismiss}
                ContractId={props.ContractId}
                ContractAddendumId={props.ContractAddendumId}
                ContractConceptId={selectedConcept?.id}
                CurrentConcepts={props.Contract?.concepts || []}
                CurrentStages={props.Addendum?.stages || []}
            />
        </div>
    );
};

export default connect(
    Fn.mapStateToProps,
    Fn.mapDispatchToProps,
)(ConceptGroupedViewComponent as any);
